import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import MeasureDropdown from '../../MeasureDropdown'
import MeasurePickerDialog from '../../MeasurePickerDialog'
import { buildMeasureSections } from '../../buildMeasureSections'
import { CALC_SOURCES, COLUMN_CATALOG_MAP, getSeriesCounterpartSource } from '../../../lib/constants'
import { canAddSeriesCandidate } from './seriesCompatibility'

// `+ Compare` chip for Trend. Built on the shared MeasureDropdown so
// the experience matches Scatter axes and Allocation measure picker:
// sectioned options with provenance, hover-tooltip reasons for
// incompatible candidates, and a "+ More measures..." footer that
// lifts a MeasurePickerDialog for measures outside the user's view.
//
// Compatibility (same-row, same measureKind, has series counterpart,
// not already a series, not at the series cap) is computed per
// candidate via canAddSeriesCandidate and surfaced as the option's
// `disabledReason` -- the dropdown renders that as a disabled item
// with the reason in a tooltip.

// Catalog-entry variant of isSeriesEligible. Used by MeasurePickerDialog
// to filter the catalog. Looser than the per-instance check (the
// dialog lets the user pick options after selecting a type), so we
// just verify the catalog's calcSource HAS a series counterpart for
// the type's default options.
function isSeriesEligibleCatalog (catalogEntry) {
  if (!catalogEntry) return false
  if (catalogEntry.kind && catalogEntry.kind !== 'material') return false
  const source = catalogEntry.calcSource ? CALC_SOURCES[catalogEntry.calcSource] : null
  const defaults = source?.defaultOptions || {}
  return !!getSeriesCounterpartSource(catalogEntry.calcSource, defaults)
}

// Best-effort label for a column the user picked from the dialog
// when it isn't in the grid's columnDefinitions. Mirrors the
// fallback chain used elsewhere (table header -> headerFn -> catalog
// label -> columnType).
function labelForInstance (instance, columnDefinitions) {
  if (!instance) return ''
  const def = (columnDefinitions || []).find(d => d.accessorKey === instance.id)
  if (def?.header) return def.header
  const catalog = COLUMN_CATALOG_MAP[instance.columnType]
  if (typeof catalog?.headerFn === 'function') {
    const fromFn = catalog.headerFn(instance.options || {})
    if (fromFn) return fromFn
  }
  return catalog?.label || instance.columnType
}

export default function CompareMenu ({
  candidates,
  primary,
  currentSeries,
  columnDefinitions,
  max,
  onAdd
}) {
  // Picker dialog state and userExtras held locally to the chip --
  // CompareMenu owns the lifetime of additions made from this entry
  // point. Once added, the instance is fed back through `onAdd` to
  // become a real series; we keep a copy in `userExtras` so the chip
  // can surface "Added" measures for future compares.
  const [pickerOpen, setPickerOpen] = useState(false)
  const [userExtras, setUserExtras] = useState([])

  const decoratedCandidates = useMemo(() => {
    // candidates is [{ columnInstance, label }] derived from
    // config.columns. Union with userExtras so picked-but-not-yet-added
    // instances stay reachable in the dropdown for swapping.
    const allInstances = [
      ...(candidates || []),
      ...userExtras.map(ci => ({ columnInstance: ci, label: labelForInstance(ci, columnDefinitions) }))
    ]
    return allInstances.map(c => {
      const decision = canAddSeriesCandidate({
        primary,
        currentSeries,
        candidate: c.columnInstance,
        columnDefinitions,
        max
      })
      return {
        id: c.columnInstance.id,
        label: c.label,
        disabledReason: decision.ok ? undefined : (decision.reason || 'Not compatible'),
        columnInstance: c.columnInstance
      }
    })
  }, [candidates, primary, currentSeries, columnDefinitions, max, userExtras])

  const measureOptions = useMemo(
    () => decoratedCandidates.map(({ id, label, disabledReason }) => ({ id, label, disabledReason })),
    [decoratedCandidates]
  )

  const sections = useMemo(() => {
    const viewIds = (candidates || []).map(c => c.columnInstance.id)
    const userIds = userExtras.map(c => c.id)
    return buildMeasureSections({ measureOptions, viewIds, suggestedIds: [], userIds })
  }, [measureOptions, candidates, userExtras])

  const atCap = (currentSeries?.length || 0) >= (max || 0)
  const anyEligible = decoratedCandidates.some(d => !d.disabledReason)
  // Trigger disables only when we're at the cap; an empty / all-disabled
  // candidate list is fine -- the dialog escape hatch is still useful.
  const triggerDisabled = atCap

  const handlePick = useCallback((id) => {
    const found = decoratedCandidates.find(d => d.id === id)
    if (!found || found.disabledReason) return
    onAdd(found.columnInstance)
  }, [decoratedCandidates, onAdd])

  const handleMeasurePicked = useCallback((instance) => {
    if (!instance) return
    // Validate the picked instance against compatibility rules. If it
    // passes, add to userExtras and become a series. If it fails, we
    // still surface it in the dropdown (with the reason) so the user
    // sees why it didn't take.
    const decision = canAddSeriesCandidate({
      primary,
      currentSeries,
      candidate: instance,
      columnDefinitions,
      max
    })
    setUserExtras(prev => prev.some(c => c.id === instance.id) ? prev : [...prev, instance])
    if (decision.ok) onAdd(instance)
  }, [primary, currentSeries, columnDefinitions, max, onAdd])

  const triggerLabel = atCap
    ? `Compare (max ${max})`
    : (!anyEligible && !sections.some(s => s.options.length > 0) ? 'Compare' : 'Compare')

  return (
    <>
      <MeasureDropdown
        sections={sections}
        variant='chip'
        triggerLabel={triggerLabel}
        triggerAriaLabel='Add a series to compare'
        triggerDisabled={triggerDisabled}
        onChange={handlePick}
        onMore={() => setPickerOpen(true)}
        moreLabel='More measures…'
        emptyMessage='No compatible measures in this view'
      />
      <MeasurePickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={handleMeasurePicked}
        title='Pick a measure to compare'
        isEligible={isSeriesEligibleCatalog}
      />
    </>
  )
}

CompareMenu.propTypes = {
  candidates: PropTypes.arrayOf(PropTypes.shape({
    columnInstance: PropTypes.object.isRequired,
    label: PropTypes.string.isRequired
  })).isRequired,
  primary: PropTypes.object,
  currentSeries: PropTypes.array,
  columnDefinitions: PropTypes.array,
  max: PropTypes.number,
  onAdd: PropTypes.func.isRequired
}
